import React, { useEffect, useState } from 'react';
import { KeyRound, Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, Button, LoadingState, Input } from '../../components/ui';
import { actionBadgeClass } from '../../constants/actionColors';

const StatCard = ({ label, value, sub, color = 'violet' }) => {
  const colors = {
    violet: 'from-violet-900/40 to-violet-800/20 border-violet-700/30',
    blue: 'from-blue-900/40 to-blue-800/20 border-blue-700/30',
    emerald: 'from-emerald-900/40 to-emerald-800/20 border-emerald-700/30',
    red: 'from-red-900/40 to-red-800/20 border-red-700/30',
    yellow: 'from-yellow-900/40 to-yellow-800/20 border-yellow-700/30',
    pink: 'from-pink-900/40 to-pink-800/20 border-pink-700/30',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`}>
      <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
      <p className="text-3xl font-bold text-white">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
};

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/super-admin/stats')
      .then(res => setStats(res.data.data))
      .catch(err => setError(err.response?.data?.message || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState tone="dark" label="Loading system overview…" />;

  if (error) return (
    <Card tone="dark" className="border-red-700/30 bg-red-900/20 text-red-400">{error}</Card>
  );

  const c = stats?.counts || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">System Overview</h2>
        <p className="text-slate-500 text-sm">Full visibility into all platform activity</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={c.totalUsers} sub={`${c.totalAdmins} admin · ${c.totalProctors} proctor · ${c.totalStudents} student`} color="violet" />
        <StatCard label="Total Exams" value={c.totalExams} color="blue" />
        <StatCard label="Questions" value={c.totalQuestions} color="emerald" />
        <StatCard label="Violations" value={c.totalViolations} color="red" />
        <StatCard label="Attempts" value={c.totalAttempts} color="yellow" />
        <StatCard label="Activity Logs" value={c.totalLogs} color="pink" />
        <StatCard label="Admins" value={c.totalAdmins} color="blue" />
        <StatCard label="Students" value={c.totalStudents} color="emerald" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Action Breakdown */}
        <Card tone="dark">
          <h3 className="text-white font-semibold mb-4">Actions (Last 30 Days)</h3>
          <div className="space-y-2">
            {(stats?.actionBreakdown || []).map(({ _id, count }) => {
              const maxCount = Math.max(...(stats?.actionBreakdown || []).map(a => a.count), 1);
              const pct = Math.round((count / maxCount) * 100);
              return (
                <div key={_id} className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded border w-28 text-center flex-shrink-0 ${actionBadgeClass(_id, 'dark')}`}>
                    {_id}
                  </span>
                  <div className="flex-1 bg-slate-800 rounded-full h-2">
                    <div
                      className="bg-violet-500 h-2 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-slate-400 text-xs w-8 text-right">{count}</span>
                </div>
              );
            })}
            {!stats?.actionBreakdown?.length && (
              <p className="text-slate-600 text-sm">No activity data yet.</p>
            )}
          </div>
        </Card>

        {/* Most Active Users */}
        <Card tone="dark">
          <h3 className="text-white font-semibold mb-4">Most Active Users (Last 30 Days)</h3>
          <div className="space-y-3">
            {(stats?.mostActiveUsers || []).map((u, idx) => (
              <div key={u._id} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-violet-700/50 text-violet-300 text-xs flex items-center justify-center font-bold">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{u.userName}</p>
                  <p className="text-slate-500 text-xs">{u.userRole}</p>
                </div>
                <span className="text-violet-400 font-bold text-sm">{u.count}</span>
              </div>
            ))}
            {!stats?.mostActiveUsers?.length && (
              <p className="text-slate-600 text-sm">No data yet.</p>
            )}
          </div>
        </Card>
      </div>

      {/* Activity per day */}
      <Card tone="dark">
        <h3 className="text-white font-semibold mb-4">Activity Per Day (Last 7 Days)</h3>
        <div className="flex items-end gap-2 h-24">
          {(stats?.activityPerDay || []).map(({ _id, count }) => {
            const maxCount = Math.max(...(stats?.activityPerDay || []).map(a => a.count), 1);
            const h = Math.max(8, Math.round((count / maxCount) * 88));
            return (
              <div key={_id} className="flex flex-col items-center gap-1 flex-1">
                <span className="text-violet-400 text-xs font-bold">{count}</span>
                <div
                  className="w-full bg-violet-600/70 rounded-t"
                  style={{ height: `${h}px` }}
                  title={`${_id}: ${count} events`}
                />
                <span className="text-slate-600 text-xs">{_id.slice(5)}</span>
              </div>
            );
          })}
          {!stats?.activityPerDay?.length && (
            <p className="text-slate-600 text-sm">No data yet.</p>
          )}
        </div>
      </Card>

      {/* Recent Logs & Password Update Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Logs (2/3 width) */}
        <Card tone="dark" className="lg:col-span-2">
          <h3 className="text-white font-semibold mb-4 text-sm">Recent Activity</h3>
          <div className="space-y-2">
            {(stats?.recentLogs || []).map((log) => (
              <div key={log._id} className="flex items-start gap-3 py-2 border-b border-slate-800/60 last:border-0">
                <span className={`text-xs font-medium px-2 py-0.5 rounded border mt-0.5 flex-shrink-0 ${actionBadgeClass(log.action, 'dark')}`}>
                  {log.action}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-300 text-sm">{log.description}</p>
                  <p className="text-slate-600 text-xs mt-0.5">
                    {log.entity} · {new Date(log.createdAt).toLocaleString()}
                  </p>
                </div>
                <span className="text-slate-600 text-xs flex-shrink-0">{log.userRole}</span>
              </div>
            ))}
            {!stats?.recentLogs?.length && (
              <p className="text-slate-600 text-sm text-center py-4">No recent activity.</p>
            )}
          </div>
        </Card>

        {/* Password Update Card (1/3 width) */}
        <Card tone="dark" className="lg:col-span-1 h-fit sticky top-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2 text-sm">
            <KeyRound className="w-5 h-5 text-yellow-500" />
            Update Your Password
          </h3>
          <PasswordUpdateForm />
        </Card>
      </div>
    </div>
  );
};

const PasswordUpdateForm = () => {
  const [newPassword, setNewPassword] = useState('');
  const [verificationKey, setVerificationKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !verificationKey) {
      return toast.error('Both fields are required');
    }

    setLoading(true);
    try {
      await api.put('/super-admin/update-password', { newPassword, verificationKey });
      toast.success('Password updated successfully');
      setNewPassword('');
      setVerificationKey('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative">
        <Input
          tone="dark"
          type={showPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          label="New Password"
          placeholder="Min. 6 characters"
          required
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[34px] text-slate-500 hover:text-slate-300"
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <Input
        tone="dark"
        type="text"
        value={verificationKey}
        onChange={(e) => setVerificationKey(e.target.value)}
        label="Verification Key"
        placeholder="Enter key"
        hint="Security safeguard for Super Admin accounts"
        required
      />
      <Button type="submit" tone="dark" variant="primary" fullWidth loading={loading}>
        Update Password
      </Button>
    </form>
  );
};

export default SuperAdminDashboard;
